// INDEX_SPLIT_B3_v1 (07.09.2026) -- Google Business + Online-Buchung: GoogleBusinessPage, BookingRulesCard, OnlineBookingPage
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { Check, CheckCircle, Copy, Edit3, ExternalLink, Globe, Scissors, useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, UserContext, isKosmetikSalon } from "../core/config.jsx";
import { Btn, Card, Skeleton, StatCard } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";


// ============================================================
// PAGE: Google My Business
// ============================================================
// GOOGLE_BUSINESS_LINK_v1 -- Setup ohne API-Key, nur mit Maps-URL + Direct-Review-Link
export const GoogleBusinessPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [mapsUrl, setMapsUrl] = useState("");
  const [reviewLink, setReviewLink] = useState("");
  const [saveMsg, setSaveMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.request("/salon/google-settings");
    if (r && r.ok && r.data) {
      setSettings(r.data);
      setMapsUrl(r.data.google_maps_url || "");
      setReviewLink(r.data.google_review_link || "");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setSaveMsg("");
    const r = await api.request("/salon/google-settings", {
      method: "PUT",
      body: JSON.stringify({
        google_maps_url: mapsUrl.trim(),
        google_review_link: reviewLink.trim(),
      }),
    });
    if (r && r.ok) {
      setSaveMsg("Gespeichert ✓");
      setEditing(false);
      await load();
    } else {
      setSaveMsg("Fehler: " + ((r && r.data && r.data.error) || "unbekannt"));
    }
    setSaving(false);
  };

  if (loading) return (
    <div>
      <Header title="Google Business" subtitle="Bewertungen verwalten" />
      <Card style={{ textAlign: "center", padding: 40 }}>Lade…</Card>
    </div>
  );

  const isConfigured = !!(settings && settings.google_maps_url);
  const rating = settings && settings.google_rating;
  const reviewCount = settings && settings.google_review_count;
  const copyReviewLink = () => {
    if (!reviewLink && !mapsUrl) return;
    navigator.clipboard.writeText(reviewLink || mapsUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div>
      <Header title="Google Business" subtitle="Bewertungen verwalten — Antworten direkt auf Google" />

      {/* SETUP-WIZARD wenn noch nichts gesetzt */}
      {!isConfigured && !editing && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <Globe size={20} color={COLORS.primary} /> Google-Profil mit Shearly verbinden
          </h3>
          <p style={{ fontSize: 13, color: COLORS.textLight, lineHeight: 1.6, margin: "0 0 16px" }}>
            Nur ein Link nötig — kein technisches Setup. So holen wir deine Bewertungen sichtbar in Shearly,
            damit du schnell auf neue Bewertungen reagieren kannst und Kunden einfach um Bewertungen bitten kannst.
          </p>

          <div style={{ background: "var(--cardHover)", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: COLORS.primary }}>So findest du deine Maps-URL (1 Min):</h4>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: COLORS.text, lineHeight: 1.8 }}>
              <li>Öffne <a href="https://www.google.com/maps" target="_blank" style={{ color: COLORS.primary }}>google.de/maps</a></li>
              <li>Suche nach <strong>deinem Salon-Namen</strong> + Stadt</li>
              <li>Klick auf deinen Salon → eigene Profil-Seite öffnet sich</li>
              <li>Klick oben auf <strong>„Teilen"</strong> → Tab „Link senden" → <strong>„Link kopieren"</strong></li>
              <li>Kopierten Link unten einfügen</li>
            </ol>
          </div>

          <div style={{ background: "var(--cardHover)", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: COLORS.primary }}>Optional: Direkter Bewertungs-Link</h4>
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 8px", lineHeight: 1.5 }}>
              Wenn du einen Google Business-Account verwaltest (
              <a href="https://business.google.com" target="_blank" style={{ color: COLORS.primary }}>business.google.com</a>),
              findest du dort einen <strong>„Mehr Bewertungen erhalten"-Link</strong> (Format <code>https://g.page/r/.../review</code>) —
              dieser bringt Kunden mit einem Klick direkt zum Bewertungs-Formular. Optional einfügen.
            </p>
          </div>

          <Btn onClick={() => setEditing(true)}><Edit3 size={14} /> Jetzt Links einfügen</Btn>
        </Card>
      )}

      {/* EDITOR */}
      {editing && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Google-Links bearbeiten</h3>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Maps-URL deines Salons *</label>
            <input value={mapsUrl} onChange={e => setMapsUrl(e.target.value)}
              placeholder="https://maps.app.goo.gl/... oder https://www.google.com/maps/place/..."
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
            <p style={{ fontSize: 11, color: COLORS.textLight, margin: "4px 0 0" }}>Aus Google Maps → „Teilen" → „Link kopieren"</p>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Direkter Bewertungs-Link (optional)</label>
            <input value={reviewLink} onChange={e => setReviewLink(e.target.value)}
              placeholder="https://g.page/r/.../review (aus Google Business Dashboard)"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
            <p style={{ fontSize: 11, color: COLORS.textLight, margin: "4px 0 0" }}>Aus business.google.com → „Mehr Bewertungen erhalten"</p>
          </div>
          {saveMsg && <div style={{ fontSize: 12, color: saveMsg.includes("✓") ? COLORS.success : COLORS.danger, marginBottom: 8 }}>{saveMsg}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="secondary" onClick={() => { setEditing(false); setMapsUrl(settings?.google_maps_url || ""); setReviewLink(settings?.google_review_link || ""); }}>Abbrechen</Btn>
            <Btn onClick={save} disabled={saving || !mapsUrl.trim()}>{saving ? "…" : "Speichern"}</Btn>
          </div>
        </Card>
      )}

      {/* CONFIGURED VIEW */}
      {isConfigured && !editing && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700 }}>Dein Google-Profil ist verbunden ✓</h3>
                {(rating !== null && rating !== undefined) ? (
                  <div style={{ fontSize: 14, color: COLORS.text }}>
                    <span style={{ color: COLORS.accent, fontSize: 18 }}>{"★".repeat(Math.round(rating))}{"☆".repeat(5 - Math.round(rating))}</span>
                    {" "}<strong>{rating}</strong>
                    {reviewCount ? <> · {reviewCount} Bewertungen</> : null}
                  </div>
                ) : (
                  <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>
                    Sterne-Rating wird angezeigt sobald Inhaber den Wert manuell setzt oder Live-Sync mit API-Key aktiviert ist.
                  </p>
                )}
              </div>
              <Btn size="sm" variant="secondary" onClick={() => setEditing(true)}><Edit3 size={12} /> Links bearbeiten</Btn>
            </div>
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
            <Card style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🌐</div>
              <h4 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700 }}>Auf Google ansehen</h4>
              <p style={{ fontSize: 11, color: COLORS.textLight, margin: "0 0 10px" }}>Dein öffentliches Profil mit allen Bewertungen</p>
              <Btn size="sm" onClick={() => window.open(settings.google_maps_url, "_blank")}><ExternalLink size={12} /> Öffnen</Btn>
            </Card>

            <Card style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✏️</div>
              <h4 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700 }}>Bewertungen beantworten</h4>
              <p style={{ fontSize: 11, color: COLORS.textLight, margin: "0 0 10px" }}>Im Google Business Dashboard antworten</p>
              <Btn size="sm" onClick={() => window.open("https://business.google.com/reviews", "_blank")}><ExternalLink size={12} /> Öffnen</Btn>
            </Card>

            <Card style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
              <h4 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700 }}>Bewertungs-Link teilen</h4>
              <p style={{ fontSize: 11, color: COLORS.textLight, margin: "0 0 10px" }}>Kunden direkt zum Bewerten schicken</p>
              <Btn size="sm" onClick={copyReviewLink}><Copy size={12} /> {linkCopied ? "Kopiert ✓" : "Link kopieren"}</Btn>
            </Card>
          </div>

          <Card>
            <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700 }}>📌 Tipps</h4>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: COLORS.textLight, lineHeight: 1.8 }}>
              <li>Beantworte <strong>jede</strong> Bewertung — auch positive (Google wertet das hoch)</li>
              <li>Bei negativen Bewertungen: ruhig + lösungsorientiert antworten, nicht streiten</li>
              <li>Nutze die SMS-Bewertungs-Anfrage (Einstellungen → SMS) — Stammkunden bekommen 1 Tag nach Termin einen Link</li>
              <li>Für Live-Synchronisation der Bewertungen brauchst du einen Google API-Key (Tier 3, kommt später)</li>
            </ul>
          </Card>
        </>
      )}
    </div>
  );
};

// ============================================================
// PAGE: Online-Buchung
// ============================================================
// BOOKING_RULES_v1 -- editierbare Buchungsregeln pro Salon
export const BookingRulesCard = ({ salonId }) => {
  const [leadMin, setLeadMin] = React.useState(15);
  const [maxWeeks, setMaxWeeks] = React.useState(26);
  const [opening, setOpening] = React.useState("");
  const [closing, setClosing] = React.useState("");
  /* SELF_SICK_TOGGLE_v1 */
  const [allowSelfSick, setAllowSelfSick] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (!salonId) return;
    api.request("/salons/" + salonId).then(r => {
      if (r && r.ok && r.data) {
        setLeadMin(r.data.booking_lead_min ?? 15);
        setMaxWeeks(r.data.booking_max_weeks ?? 26);
        setOpening(r.data.opening_time || "");
        setClosing(r.data.closing_time || "");
        setAllowSelfSick(r.data.allow_self_sick_leave !== 0); // SELF_SICK_TOGGLE_v1
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [salonId]);

  const save = async () => {
    setSaving(true);
    const payload = {
      booking_lead_min: Math.max(0, Math.min(1440, parseInt(leadMin) || 15)),
      booking_max_weeks: Math.max(1, Math.min(104, parseInt(maxWeeks) || 26)),
      allow_self_sick_leave: allowSelfSick ? 1 : 0,  // SELF_SICK_TOGGLE_v1
    };
    const r = await api.request("/salons/" + salonId, { method: "PUT", body: JSON.stringify(payload) });
    setSaving(false);
    if (r && r.ok) {
      setDirty(false);
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 3000);
    } else {
      alert("Speichern fehlgeschlagen.");
    }
  };

  if (loading) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}><Skeleton width={180} height={18} /></div>
        <Skeleton width="100%" height={70} />
      </Card>
    );
  }

  // Prozentanzeigen-Hilfe: lead in Min in lesbares Label umwandeln
  const leadLabel = leadMin < 60 ? `${leadMin} Min` : `${(leadMin/60).toFixed(leadMin%60===0?0:1)}h`;
  const weeksLabel = maxWeeks < 4 ? `${maxWeeks} Wochen` : `${(maxWeeks/4).toFixed(maxWeeks%4===0?0:1)} Monate`;

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Buchungsregeln</h3>
        {savedAt && <span style={{ fontSize: 11, color: "#10B981", fontWeight: 600 }}>✓ gespeichert</span>}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Vorlaufzeit <span style={{ color: COLORS.textLight, fontWeight: 400 }}>· {leadLabel}</span>
        </label>
        <p style={{ fontSize: 11, color: COLORS.textLight, margin: "0 0 8px", lineHeight: 1.5 }}>
          Wie kurzfristig dürfen Kunden buchen? Beispiel: <strong>15 Min</strong> bedeutet, ein Kunde kann frühestens für in 15 Min einen Termin nehmen — auch wenn der Mitarbeiter gerade frei ist. Niedrige Werte = mehr Last-Minute-Umsatz, höhere Werte = mehr Vorlauf zum Vorbereiten. Gilt für Online-Buchung, KI-Chat und KI-Telefonie gleich.
        </p>
        <input type="number" value={leadMin} min={0} max={1440} step={5}
          onChange={e => { setLeadMin(e.target.value); setDirty(true); }}
          style={{ width: 120, padding: "8px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, background: "var(--card)", color: COLORS.text, marginRight: 8 }} />
        <span style={{ fontSize: 12, color: COLORS.textLight }}>Minuten</span>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Max. Buchungszeitraum <span style={{ color: COLORS.textLight, fontWeight: 400 }}>· {weeksLabel}</span>
        </label>
        <p style={{ fontSize: 11, color: COLORS.textLight, margin: "0 0 8px", lineHeight: 1.5 }}>
          Wie weit in die Zukunft dürfen Termine gebucht werden? Stammkunden buchen gern schon im Oktober den Weihnachts-Termin — daher <strong>26 Wochen (= 6 Monate)</strong> als Standard. Kürzer halten wenn du nicht so weit voraus planen willst.
        </p>
        <input type="number" value={maxWeeks} min={1} max={104} step={1}
          onChange={e => { setMaxWeeks(e.target.value); setDirty(true); }}
          style={{ width: 120, padding: "8px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, background: "var(--card)", color: COLORS.text, marginRight: 8 }} />
        <span style={{ fontSize: 12, color: COLORS.textLight }}>Wochen</span>
      </div>

      <div style={{ padding: 10, background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 14, fontSize: 11.5, color: "var(--textLight)", lineHeight: 1.6 }}>
        <strong style={{ color: "var(--text)" }}>Öffnungszeiten</strong> {opening || "—"} bis {closing || "—"} (in Salon-Einstellungen). Außerhalb dieser Zeiten ist nichts buchbar — auch nicht wenn Mitarbeiter Schicht haben.
      </div>

      {/* SELF_SICK_TOGGLE_v1 -- Owner kann Self-Krankmeldung pro Salon deaktivieren */}
      <div style={{ marginTop: 8, marginBottom: 14, padding: 12, background: "var(--cardHover)", borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={allowSelfSick}
            onChange={e => { setAllowSelfSick(e.target.checked); setDirty(true); }}
            style={{ marginTop: 3, accentColor: COLORS.primary, flexShrink: 0 }} />
          <span>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, display: "block", marginBottom: 4 }}>
              Mitarbeiter dürfen sich selbst krank melden
            </span>
            <span style={{ fontSize: 11.5, color: COLORS.textLight, lineHeight: 1.55 }}>
              {allowSelfSick
                ? <>Mitarbeiter können in <i>Mein Profil</i> mit einem Klick <i>Heute krank melden</i> / <i>Verspätung</i> / <i>Früher Schluss</i> setzen. Inhaber wird per Audit-Log informiert.</>
                : <><strong>Aus.</strong> Mitarbeiter müssen sich direkt bei dir / Empfang abmelden. Im Profil sehen sie keinen Krankmeldungs-Button mehr. Du / Manager können weiterhin Überschreibungen für Mitarbeiter setzen.</>}
            </span>
          </span>
        </label>
      </div>

      <Btn variant="primary" onClick={save} disabled={saving || !dirty}>
        <Check size={14} /> {saving ? "Speichere…" : "Speichern"}
      </Btn>
    </Card>
  );
};

export const OnlineBookingPage = ({ services = [], employees = [] }) => {
  const [widgetStyle, setWidgetStyle] = useState("embedded");
  // BOOKING_LINK_DYNAMIC_v1 -- Slug aus eigenem Salon laden statt hardcoded
  const _ctxUser = React.useContext(UserContext);
  const _salonId = _ctxUser && _ctxUser.salon_id;
  const [salonSlug, setSalonSlug] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  useEffect(() => {
    if (!_salonId) return;
    api.request("/salons/" + _salonId).then(r => {
      if (r && r.ok && r.data && r.data.slug) setSalonSlug(r.data.slug);
    }).catch(() => {});
  }, [_salonId]);
  const bookingUrl = salonSlug ? `https://shearly.pro/b/${salonSlug}` : "";
  const bookingDisplay = salonSlug ? `shearly.pro/b/${salonSlug}` : "wird geladen…";
  const copyLink = () => {
    if (!bookingUrl) return;
    navigator.clipboard.writeText(bookingUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  return (
    <div>
      <Header title="Online-Buchung" subtitle="Kunden buchen direkt über Ihre Website" />

      {/* Public Booking Link — BOOKING_LINK_DYNAMIC_v1: dynamisch aus eigenem Salon-Slug */}
      <Card style={{ marginBottom: 16, background: `linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.primaryLight}08)`, border: `1px solid ${COLORS.primary}20` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700 }}>Dein Buchungslink</h3>
            <p style={{ margin: 0, fontSize: 12, color: COLORS.textLight }}>
              Teile diesen Link auf Instagram, Google, deiner Website, per WhatsApp oder als QR-Code in deinem Salon.
              Kunden buchen damit ihre Termine selbst — auch nachts und am Wochenende.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <code style={{ padding: "8px 14px", background: "var(--card)", borderRadius: 8, fontSize: 13, fontWeight: 600, border: `1px solid ${COLORS.border}`, color: COLORS.text }}>
              {bookingDisplay}
            </code>
            {salonSlug && (
              <>
                <Btn size="sm" onClick={copyLink}>
                  <Copy size={12} /> {linkCopied ? "Kopiert ✓" : "Kopieren"}
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => window.open(bookingUrl, "_blank")}>
                  <Globe size={12} /> Öffnen
                </Btn>
              </>
            )}
          </div>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <StatCard icon={Globe} label="Online-Buchungen (Monat)" value="47" trend={18} color={COLORS.primary} />
        <StatCard icon={CheckCircle} label="Conversion-Rate" value="68%" color={COLORS.success} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1.2fr 1fr", gap: 16 }}>
        {/* Widget Preview */}
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Widget-Vorschau</h3>
          <div style={{ border: `2px dashed ${COLORS.border}`, borderRadius: 12, padding: 24, background: "var(--bg)" }}>
            <div style={{ maxWidth: window.innerWidth < 768 ? "100%" : 360, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <Scissors size={24} color={COLORS.primary} />
                <h4 style={{ margin: "8px 0 4px", fontSize: 16 }}>Termin buchen</h4>
                <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>Salon Schönheit — Online-Buchung</p>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Service wählen</label>
                <select style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <option>— Bitte wählen —</option>
                  {services.map(s => <option key={s.id}>{s.name} — ab {s.basePrice} €</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Mitarbeiter (optional)</label>
                <select style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <option>Kein Wunsch</option>
                  {employees.map(e => <option key={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Datum & Uhrzeit</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="date" style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
                  <select style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                    <option>09:00</option><option>09:30</option><option>10:00</option><option>10:30</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Name & Telefon</label>
                <input placeholder="Ihr Name" style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, marginBottom: 6, boxSizing: "border-box" }} />
                <input placeholder="Telefonnummer" style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, boxSizing: "border-box" }} />
              </div>
              <button style={{ width: "100%", padding: "12px", background: COLORS.primary, color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Termin buchen
              </button>
            </div>
          </div>
        </Card>

        {/* Config */}
        <div>
          <Card style={{ marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Einbetten auf Ihrer Website</h3>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {["embedded", "popup", "fullpage"].map(t => (
                <Btn key={t} size="sm" variant={widgetStyle === t ? "primary" : "secondary"} onClick={() => setWidgetStyle(t)}>
                  {t === "embedded" ? "Eingebettet" : t === "popup" ? "Popup-Button" : "Eigene Seite"}
                </Btn>
              ))}
            </div>
            <div style={{ padding: 12, background: "#1F2937", borderRadius: 8, marginBottom: 12 }}>
              <code style={{ fontSize: 11, color: "#10B981", lineHeight: 1.8 }}>
                {widgetStyle === "embedded"
                  ? '<iframe src="https://book.shearly.de/salon-schoenheit" width="100%" height="600" frameborder="0"></iframe>'
                  : widgetStyle === "popup"
                  ? '<script src="https://cdn.shearly.de/widget.js" data-salon="salon-schoenheit"><\/script>'
                  : 'https://book.shearly.de/salon-schoenheit'}
              </code>
            </div>
            <Btn size="sm" variant="secondary"><Copy size={12} /> Code kopieren</Btn>
          </Card>

          {/* BOOKING_RULES_v1 -- editierbare Karte mit Backend-Persistenz */}
          <BookingRulesCard salonId={_salonId} />

          <Card style={{ marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Weitere Regeln</h3>
            <div style={{ fontSize: 13, lineHeight: 2.2 }}>
              {[
                { label: "Online-Stornierung erlaubt bis", val: "individuell einstellbar (No-Show-Regeln)" },
                { label: "Vorauszahlung bei Online-Buchung", val: "Optional (30%)" },
                { label: "Automatische Bestätigung", val: "Ja" },
                { label: "Warteliste bei ausgebuchten Slots", val: "Aktiv" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{r.label}</span>
                  <strong>{r.val}</strong>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Warteliste</h3>
            <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 12 }}>
              Kunden können sich auf die Warteliste setzen wenn der Wunschtermin voll ist. Bei Stornierung → automatische SMS an den Nächsten auf der Liste.
            </div>
            {(isKosmetikSalon() ? [
              { name: "Lisa M.", service: "Gesichtsbehandlung", preferred: "Fr. 10:00", since: "2 Tage" },
              { name: "Sandra W.", service: "Maniküre", preferred: "Sa. 14:00", since: "1 Tag" },
            ] : [
              { name: "Lisa M.", service: "Balayage", preferred: "Fr. 10:00 bei Tom", since: "2 Tage" },
              { name: "Sandra W.", service: "Damenschnitt", preferred: "Sa. 14:00 bei Sarah", since: "1 Tag" },
            ]).map((w, i) => (
              <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                <div><strong>{w.name}</strong> — {w.service}</div>
                <div style={{ color: COLORS.textLight }}>Wunsch: {w.preferred} | seit {w.since}</div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
};